/**
 * Durable Asset Inventory / Information Systems filters for Asset & System Accountability.
 * URL is the source of truth.
 *
 * Inventory:
 *   tab=inventory&owner=assigned|unassigned
 *   tab=inventory&informationSystem=linked|unlinked
 * Information Systems:
 *   tab=information-systems&owner=assigned|unassigned
 */

export type OwnerFilterMode = 'assigned' | 'unassigned'
export type InformationSystemLinkMode = 'linked' | 'unlinked'

export type AssetSystemAccountabilityFilters = {
  owner: OwnerFilterMode | null
  informationSystem: InformationSystemLinkMode | null
}

export type AccountabilityHrefOpts = {
  tab?: 'inventory' | 'information-systems'
  owner?: OwnerFilterMode | null
  informationSystem?: InformationSystemLinkMode | null
}

export type AssetAccountabilityFields = {
  id?: string
  owner?: { id?: string; name?: string } | null
  hasOwner?: boolean
  linkedToInformationSystem?: boolean
  ownerId?: string | null
}

const OWNER_MODES = new Set<OwnerFilterMode>(['assigned', 'unassigned'])
const IS_LINK_MODES = new Set<InformationSystemLinkMode>(['linked', 'unlinked'])

export function normalizeOwnerFilter(raw: string | null | undefined): OwnerFilterMode | null {
  const v = String(raw || '')
    .trim()
    .toLowerCase()
  if (!v) return null
  if (OWNER_MODES.has(v as OwnerFilterMode)) return v as OwnerFilterMode
  if (v === 'missing' || v === 'none' || v === 'empty') return 'unassigned'
  if (v === 'present' || v === 'owned' || v === 'with-owner') return 'assigned'
  return null
}

export function normalizeInformationSystemLinkFilter(
  raw: string | null | undefined,
): InformationSystemLinkMode | null {
  const v = String(raw || '')
    .trim()
    .toLowerCase()
  if (!v) return null
  if (IS_LINK_MODES.has(v as InformationSystemLinkMode)) return v as InformationSystemLinkMode
  if (v === 'none' || v === 'missing' || v === 'no') return 'unlinked'
  if (v === 'yes' || v === 'any') return 'linked'
  return null
}

export function parseAssetSystemAccountabilityFilters(
  searchParams: URLSearchParams | { get: (k: string) => string | null },
): AssetSystemAccountabilityFilters {
  return {
    owner: normalizeOwnerFilter(searchParams.get('owner')),
    informationSystem: normalizeInformationSystemLinkFilter(searchParams.get('informationSystem')),
  }
}

export function hasActiveAccountabilityFilters(f: AssetSystemAccountabilityFilters): boolean {
  return Boolean(f.owner || f.informationSystem)
}

export function hasActiveInventoryAccountabilityFilters(
  f: AssetSystemAccountabilityFilters,
): boolean {
  return Boolean(f.owner || f.informationSystem)
}

export function assetHasDirectOwner(asset: AssetAccountabilityFields): boolean {
  if (asset.hasOwner === true) return true
  if (asset.hasOwner === false) return false
  if (asset.owner && (asset.owner.id || asset.owner.name)) return true
  if (asset.ownerId && String(asset.ownerId).trim()) return true
  return false
}

export function assetMatchesAccountabilityFilters(
  asset: AssetAccountabilityFields,
  filters: AssetSystemAccountabilityFilters,
): boolean {
  if (!hasActiveInventoryAccountabilityFilters(filters)) return true

  if (filters.owner === 'assigned') {
    if (!assetHasDirectOwner(asset)) return false
  } else if (filters.owner === 'unassigned') {
    if (assetHasDirectOwner(asset)) return false
  }

  if (filters.informationSystem === 'linked') {
    if (!asset.linkedToInformationSystem) return false
  } else if (filters.informationSystem === 'unlinked') {
    if (asset.linkedToInformationSystem) return false
  }

  return true
}

export function filterAssetsByAccountability<T extends AssetAccountabilityFields>(
  assets: T[],
  filters: AssetSystemAccountabilityFilters,
): T[] {
  if (!hasActiveInventoryAccountabilityFilters(filters)) return assets
  return assets.filter((a) => assetMatchesAccountabilityFilters(a, filters))
}

/** Build durable `/assets?...` URL for accountability navigation. */
export function buildAccountabilityInventoryUrl(opts: AccountabilityHrefOpts = {}): string {
  const params = buildAccountabilitySearchParams(opts)
  const tab = opts.tab || 'inventory'
  const hash =
    tab === 'information-systems' ? '#assets-information-systems' : '#assets-inventory-results'
  return `/assets?${params.toString()}${hash}`
}

export function buildAccountabilitySearchParams(
  opts: AccountabilityHrefOpts = {},
): URLSearchParams {
  const params = new URLSearchParams()
  const tab = opts.tab || 'inventory'
  params.set('tab', tab)

  const owner = normalizeOwnerFilter(opts.owner ?? null)
  const isLink = normalizeInformationSystemLinkFilter(opts.informationSystem ?? null)

  if (owner) params.set('owner', owner)
  if (isLink && tab === 'inventory') params.set('informationSystem', isLink)

  return params
}

export function clearAccountabilityParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params)
  next.delete('owner')
  next.delete('informationSystem')
  return next
}

export function removeAccountabilityParam(
  params: URLSearchParams,
  key: 'owner' | 'informationSystem',
): URLSearchParams {
  const next = new URLSearchParams(params)
  next.delete(key)
  return next
}
